"use client";

// React and Next.js core imports
// (none in this case)

// Global styles
// (none in this case)

// Providers/Context
// (none in this case)

// Components
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import useLogin from "@/components/hooks/login/useLogin.hook";
import { Eye, EyeOff, LoaderCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import { ClipboardEvent, KeyboardEvent } from "react";

const LoginPage = () => {
  const t = useTranslations("Login");
  const {
    form,
    onSubmit,
    loginLoading,
    showPassword,
    togglePasswordVisibility,
    requiresTwoFactor,
    otpDigits = [],
    handleOtpDigitChange = () => {},
    resendOtp = () => {},
    onVerifyOtp = () => {},
    verifyOtpLoading = false,
    resendOtpLoading = false,
  } = useLogin();

  const otpValue = otpDigits.join("");

  const handleOtpKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      const previousInput = document.getElementById(`otp-${index - 1}`);
      previousInput?.focus();
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const onlyDigits = value.replace(/\D/g, "");

    if (onlyDigits.length > 1) {
      const chars = onlyDigits.slice(0, otpDigits.length - index).split("");
      chars.forEach((char, offset) => {
        handleOtpDigitChange(index + offset, char);
      });
      const targetIndex = Math.min(index + chars.length, otpDigits.length - 1);
      const targetInput = document.getElementById(`otp-${targetIndex}`);
      targetInput?.focus();
      return;
    }

    handleOtpDigitChange(index, onlyDigits);
    if (onlyDigits && index < otpDigits.length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpPaste = (
    event: ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedText) return;

    const chars = pastedText.slice(0, otpDigits.length - index).split("");
    chars.forEach((char, offset) => {
      handleOtpDigitChange(index + offset, char);
    });

    const targetIndex = Math.min(index + chars.length, otpDigits.length - 1);
    const targetInput = document.getElementById(`otp-${targetIndex}`);
    targetInput?.focus();
  };

  return (
    <div className="flex items-center justify-center w-full p-4">
      <div className="w-full max-w-[400px] p-8 space-y-8 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center">
          {requiresTwoFactor ? "Verify OTP" : "Login"}
        </h1>
        {!requiresTwoFactor ? (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={field.name}>Username</FormLabel>
                    <FormControl>
                      <Input placeholder="Username or Email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor={field.name}>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="Password"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <EyeOff className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={loginLoading}>
                {t("login")}
                {loginLoading && (
                  <LoaderCircle className="animate-spin ml-2 w-4 h-4" />
                )}
              </Button>
            </form>
          </Form>
        ) : (
          <div className="space-y-6">
            <p className="text-sm text-center text-muted-foreground">
              Enter the 5-digit OTP sent to your email.
            </p>
            <div className="flex items-center justify-center gap-2">
              {otpDigits.map((digit, index) => (
                <Input
                  key={`otp-${index}`}
                  id={`otp-${index}`}
                  value={digit}
                  maxLength={1}
                  inputMode="numeric"
                  className="h-12 w-12 text-center text-lg font-semibold"
                  onChange={(event) => handleOtpChange(index, event.target.value)}
                  onKeyDown={(event) => handleOtpKeyDown(event, index)}
                  onPaste={(event) => handleOtpPaste(event, index)}
                />
              ))}
            </div>
            <Button
              type="button"
              className="w-full"
              disabled={otpValue.length !== 5 || verifyOtpLoading}
              onClick={onVerifyOtp}
            >
              Verify OTP
              {verifyOtpLoading && (
                <LoaderCircle className="animate-spin ml-2 w-4 h-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={resendOtp}
              disabled={resendOtpLoading}
            >
              Resend OTP
              {resendOtpLoading && (
                <LoaderCircle className="animate-spin ml-2 w-4 h-4" />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
